import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

/** v3 DemoCountdown parity: demo data resets on every EVEN UTC hour (0, 2, 4, …). */
export const isAtDemoResetBoundary = (now: Date): boolean =>
  now.getUTCHours() % 2 === 0 && now.getUTCMinutes() === 0;

export const msUntilNextDemoReset = (now: Date): number => {
  const hour = now.getUTCHours();
  const hoursToAdd = hour % 2 === 0 ? 2 : 1;
  const target = new Date(now);
  target.setUTCHours(hour + hoursToAdd, 0, 0, 0);
  return target.getTime() - now.getTime();
};

export const formatCountdown = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0
    ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`;
};

/**
 * v3 DemoCountdown parity, hand-rolled instead of `react-countdown`: every
 * tick recomputes the time to the next even-UTC-hour boundary, so hitting
 * the boundary and "mounting exactly on a boundary" collapse into ONE check
 * (`isAtDemoResetBoundary`) instead of two separate v3 code paths.
 */
export const useDemoResetCountdown = (enabled: boolean): string => {
  const navigate = useNavigate();
  const [remainingMs, setRemainingMs] = useState(() => msUntilNextDemoReset(new Date()));

  useEffect(() => {
    if (!enabled) return;

    const tick = () => {
      const now = new Date();
      if (isAtDemoResetBoundary(now)) {
        navigate('/maintenance/resetting-demo');
        return;
      }
      setRemainingMs(msUntilNextDemoReset(now));
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [enabled, navigate]);

  return formatCountdown(remainingMs);
};
